import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Calendar, Clock, ChevronRight, ChevronLeft, Tag, Filter } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper functions to avoid nested ternaries
const getLocalizedText = (language, esText, enText, itText) => {
  if (language === 'es') return esText;
  if (language === 'it') return itText;
  return enText;
};

const getAllCategoriesText = (language) => 
  getLocalizedText(language, 'Todas las categorías', 'All categories', 'Tutte le categorie');

const getArticleCountText = (language, count) => {
  const singular = getLocalizedText(language, 'artículo', 'article', 'articolo');
  const plural = getLocalizedText(language, 'artículos', 'articles', 'articoli');
  return count === 1 ? singular : plural;
};

const getNoArticlesTitle = (language) =>
  getLocalizedText(language, 'No hay artículos todavía', 'No articles yet', 'Nessun articolo ancora');

const getNoArticlesDesc = (language) =>
  getLocalizedText(language, 'Pronto publicaremos contenido interesante', 'We will publish interesting content soon', 'Presto pubblicheremo contenuti interessanti');

// Breadcrumbs component for SEO
function Breadcrumbs({ items }) {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url
    }))
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <nav aria-label="Breadcrumb" className="text-sm text-secondary mb-6">
        <ol className="flex items-center gap-2 flex-wrap">
          {items.map((item, index) => (
            <li key={index} className="flex items-center gap-2">
              {index > 0 && <ChevronRight className="h-3 w-3" />}
              {item.current ? (
                <span className="text-primary font-medium">{item.name}</span>
              ) : (
                <Link to={item.path} className="hover:text-gold transition-colors" style={{ color: 'inherit' }}>
                  {item.name}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </nav>
    </>
  );
}

// Blog card component
function BlogCard({ article, language }) {
  const title = article.title?.[language] || article.title?.es || '';
  const excerpt = article.excerpt?.[language] || article.excerpt?.es || '';
  const slug = article.slug?.[language] || article.slug?.es || article.id;
  const categoryName = article.category?.name?.[language] || article.category?.name?.es || '';
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'es' ? 'es-ES' : language === 'it' ? 'it-IT' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <article 
      className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden group"
      data-testid="blog-card"
    >
      <Link to={`/blog/${slug}`}>
        {article.featured_image ? (
          <div className="aspect-video overflow-hidden">
            <img 
              src={article.featured_image} 
              alt={title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              loading="lazy"
            />
          </div>
        ) : (
          <div className="aspect-video bg-gradient-to-br from-gold/20 to-gold/5 flex items-center justify-center">
            <span className="text-6xl opacity-30">📝</span>
          </div>
        )}
        
        <div className="p-5">
          {categoryName && (
            <span 
              className="inline-block px-3 py-1 text-xs font-medium rounded-full mb-3"
              style={{ backgroundColor: '#D4A574', color: 'white' }}
            >
              {categoryName}
            </span>
          )}
          
          <h2 className="font-serif text-xl font-bold text-primary mb-2 group-hover:text-gold transition-colors line-clamp-2">
            {title}
          </h2>
          
          <p className="text-secondary text-sm mb-4 line-clamp-3">
            {excerpt}
          </p>
          
          <div className="flex items-center justify-between text-xs text-secondary">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {formatDate(article.published_at)}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {article.reading_time} min
              </span>
            </div>
          </div>
        </div>
      </Link>
    </article>
  );
}

export default function BlogPage() {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  
  const currentCategory = searchParams.get('category') || '';
  const currentPage = parseInt(searchParams.get('page') || '1', 10);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [articlesRes, categoriesRes] = await Promise.all([
          axios.get(`${API_URL}/api/blog/articles`, {
            params: { 
              lang: language, 
              category: currentCategory || undefined,
              page: currentPage,
              limit: 9
            }
          }),
          axios.get(`${API_URL}/api/blog/categories`)
        ]);
        
        setArticles(articlesRes.data.articles || []);
        setPagination({
          page: articlesRes.data.page,
          pages: articlesRes.data.pages,
          total: articlesRes.data.total
        });
        setCategories(categoriesRes.data || []);
      } catch (error) {
        console.error('Error fetching blog data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [language, currentCategory, currentPage]);

  const handleCategoryChange = (value) => {
    const params = new URLSearchParams(searchParams);
    if (value && value !== 'all') {
      params.set('category', value);
    } else {
      params.delete('category');
    }
    params.delete('page');
    setSearchParams(params);
  };

  const handlePageChange = (newPage) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', newPage.toString());
    setSearchParams(params);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const breadcrumbs = [
    { name: t('nav.home') || 'Inicio', path: '/', url: 'https://tantra-map.com/' },
    { name: 'Blog', path: '/blog', url: 'https://tantra-map.com/blog', current: true }
  ];

  const pageTitle = {
    es: 'Blog | Artículos sobre Tantra y Bienestar | Tantra Map',
    en: 'Blog | Articles about Tantra and Wellness | Tantra Map',
    it: 'Blog | Articoli su Tantra e Benessere | Tantra Map'
  };

  const pageDescription = {
    es: 'Descubre artículos sobre tantra, meditación, respiración y bienestar. Guías, consejos y entrevistas con profesionales del tantra.',
    en: 'Discover articles about tantra, meditation, breathing and wellness. Guides, tips and interviews with tantra professionals.',
    it: 'Scopri articoli su tantra, meditazione, respirazione e benessere. Guide, consigli e interviste con professionisti del tantra.'
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle[language] || pageTitle.es}</title>
        <meta name="description" content={pageDescription[language] || pageDescription.es} />
        <meta property="og:title" content={pageTitle[language] || pageTitle.es} />
        <meta property="og:description" content={pageDescription[language] || pageDescription.es} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://tantra-map.com/${language}/blog`} />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href={`https://tantra-map.com/${language}/blog`} />
      </Helmet>

      <div className="min-h-screen bg-cream py-8 px-4" style={{ backgroundColor: '#FDFBF7' }} data-testid="blog-page">
        <div className="max-w-6xl mx-auto">
          <Breadcrumbs items={breadcrumbs} />
          
          {/* Header */}
          <div className="text-center mb-10">
            <h1 className="font-serif text-4xl md:text-5xl font-bold text-primary mb-4">
              Blog
            </h1>
            <p className="text-secondary text-lg max-w-2xl mx-auto">
              {language === 'es' && 'Artículos, guías y consejos sobre tantra y bienestar'}
              {language === 'en' && 'Articles, guides and tips about tantra and wellness'}
              {language === 'it' && 'Articoli, guide e consigli su tantra e benessere'}
            </p>
          </div>
          
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-8">
            <div className="flex-1">
              <Select value={currentCategory || 'all'} onValueChange={handleCategoryChange}>
                <SelectTrigger className="bg-white">
                  <div className="flex items-center gap-2">
                    <Filter className="h-4 w-4 text-secondary" />
                    <SelectValue placeholder={getAllCategoriesText(language)} />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    {getAllCategoriesText(language)}
                  </SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.slug}>
                      {cat.name?.[language] || cat.name?.es}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="text-sm text-secondary flex items-center">
              {pagination.total} {getArticleCountText(language, pagination.total)}
            </div>
          </div>
          
          {/* Articles Grid */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={`skeleton-${i}`} className="bg-white rounded-xl overflow-hidden animate-pulse">
                  <div className="aspect-video bg-gray-200" />
                  <div className="p-5 space-y-3">
                    <div className="h-4 bg-gray-200 rounded w-20" />
                    <div className="h-6 bg-gray-200 rounded w-3/4" />
                    <div className="h-4 bg-gray-200 rounded" />
                    <div className="h-4 bg-gray-200 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : articles.length === 0 ? (
            <div className="text-center py-16">
              <span className="text-6xl mb-4 block">📝</span>
              <h3 className="text-xl font-medium text-primary mb-2">
                {getNoArticlesTitle(language)}
              </h3>
              <p className="text-secondary">
                {getNoArticlesDesc(language)}
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {articles.map(article => (
                  <BlogCard key={article.id} article={article} language={language} />
                ))}
              </div>
              
              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="flex justify-center items-center gap-2 mt-10">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page <= 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  
                  {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(page => (
                    <Button
                      key={page}
                      variant={page === pagination.page ? "default" : "outline"}
                      size="sm"
                      onClick={() => handlePageChange(page)}
                      className={page === pagination.page ? 'bg-gold text-white' : ''}
                      style={page === pagination.page ? { backgroundColor: '#D4A574' } : {}}
                    >
                      {page}
                    </Button>
                  ))}
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page >= pagination.pages}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
